import { curry } from 'lib/util'
import { get, reset } from 'lib/sequence/component/state/value'
import search from 'app/_shared/events/search'
import find from 'lib/sequence/component/children/find'
import { showBlockingDialog } from 'app/_shared/events/dialog'
import { persistCheck } from 'app/_shared/events'
import { check, uncheck } from 'lib/sequence/component/state/checked'

/**
 * Displays a warning dialog informing the user that a reference measurement already exists with
 * the introduced form values.
 *
 * @param module  current module
 */
const showOverwriteWarningDialog = async (module: Gaia.AppModule.Spec) => {
  return await showBlockingDialog(module, null, {
    title: {
      ns: 'apmGo',
      key: 'dialog.overwriteReferenceWarning.title',
      defaultValue: 'Warning',
    },
    text: {
      ns: 'apmGo',
      key: 'dialog.overwriteReferenceWarning.text',
      defaultValue: 'A reference measurement already exists with this same data. Are you sure you want to overwrite it?'
    }
  })
}

/**
 * Forces `checked: false` state in `component` and updates the view.
 *
 * @param module    current module
 * @param component checkbox
 */
const forceUncheckAndUpdateUI = async (
  module: Gaia.AppModule.Spec,
  component: Gaia.Component.Spec,
) => {
  check(component)
  await module[G.ADAPTER][G.UI].update(module)
  uncheck(component)
  reset(component)
  await module[G.ADAPTER][G.UI].update(module)
}

/**
 * Gathers the current form's values (project, tank, location, frequency and usPower) and uses them
 * to attempt to find an existing reference measurement. If any form value is missing, returns null.
 *
 * @param module  current module
 * @returns an existing reference measurement or null
 */
const searchReferenceMeasurement = async (module: Gaia.AppModule.Spec): Promise<object | null> => {
  const actionComponent = module[G.STATE][G.ACTION][G.COMPONENT]
  const { project, tank, location, frequency, usPower } = find(actionComponent)
  const fields = { project, tank, location, frequency, usPower }

  const items = Object.keys(fields).reduce((acc, key) => ({
    ...acc, [key]: get(fields[key]),
  }), {})

  const valueIsMissing = Object.values(items).some(value => !value && value !== 0)

  if (valueIsMissing) {
    return null
  }

  const filter = { ...items, reference: true }
  const type = 'x_eco_apmmeasurement'
  const sort = [["submit_timestamp<number>", "desc"]]
  const options = { type, filter, sort }

  const [measurement] = await search(null, module, actionComponent, options)

  return !measurement || measurement?.key === -1 ? null : measurement
}

/**
 * Shows a warning if the checkbox `component` is set to `checked` and a reference already exists
 * with the same values as those entered in the form.
 *
 * If the user cancels the dialog, the checkbox `component` is automatically unchecked.
 *
 * @param module    current module
 * @param component event source
 * @param event     triggered event
 */
const toggleSetAsReference = async (
  module: Gaia.AppModule.Spec,
  component: Gaia.Component.Spec,
  event: Gaia.PlatformEvent,
) => {
  await persistCheck(module, component, event)
  await module[G.ADAPTER][G.UI].update(module)

  if (event.detail.checked) {
    const measurement = await searchReferenceMeasurement(module)

    if (measurement && !await showOverwriteWarningDialog(module)) {
      await forceUncheckAndUpdateUI(module, component)
    }
  }
}

export default curry(toggleSetAsReference)
